import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Check } from 'lucide-react';
import type { CompetitorData } from '@/routes/compare/data';

interface VerdictBlockProps {
  data: CompetitorData;
}

/** Derive a factual starting-price phrase from the competitor's plan data. */
function startingPricePhrase(data: CompetitorData): string {
  const paid = data.plans.filter((p) => p.price > 0);
  const free = data.plans.find((p) => p.price === 0);
  const cheapest = paid.length ? paid.reduce((a, b) => (a.price <= b.price ? a : b)) : null;
  if (free && cheapest) {
    return `free tier available, paid plans from $${cheapest.price}/agent/month`;
  }
  if (cheapest) {
    return `paid plans from $${cheapest.price}/agent/month`;
  }
  return data.pricingModel.toLowerCase();
}

export function VerdictBlock({ data }: VerdictBlockProps) {
  const pricePhrase = startingPricePhrase(data);

  return (
    <section className="pt-12 md:pt-16 pb-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-medium text-zinc-600 mb-4">
            <Sparkles className="size-3" /> Comparison
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight text-zinc-900 mb-3">
            Kora vs {data.name}
          </h1>
          <p className="text-base text-zinc-500 max-w-2xl mx-auto">
            {data.tagline}. An honest look at pricing, features, and which tool fits your team.
          </p>
        </div>

        {/* Answer capsule — direct, data-derived answer for search and AI engines */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 mb-8 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="size-3.5 text-zinc-400" />
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Quick answer
            </span>
          </div>
          <p className="text-sm text-zinc-700 leading-relaxed">
            {data.name} is {data.bestFor.replace(/\.$/, '')}, with {data.pricingModel.toLowerCase()} pricing ({pricePhrase}).{' '}
            Kora is an AI helpdesk and issue tracker priced per ticket ($0.10 per ticket) with unlimited members and no monthly plans —{' '}
            AI ticket structuring, SLA tracking and automation rules are free, and the AI Analyst is 1 credit per answer.{' '}
            {data.name} fits teams that need {data.bestFor.toLowerCase().replace(/^[a-z] /, '')}; teams that want per-ticket pricing with no seat fees typically choose Kora.
          </p>
        </div>

        {/* Verdict cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Kora card */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <img width={24} height={24} src="/assets/logo_square.svg" alt="Kora logo" className="size-6" />
              <span className="text-base font-semibold text-zinc-900">Kora</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                Best for
              </span>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              {data.verdictKora}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5">
              {data.koraWins.slice(0, 3).map((w) => (
                <span key={w} className="inline-flex items-center gap-1 text-xs text-zinc-500">
                  <Check className="size-3 text-green-500" /> {w}
                </span>
              ))}
            </div>
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Try Kora free <ArrowRight className="size-3.5" />
            </Link>
          </div>

          {/* Competitor card */}
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="size-6 rounded-md bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600">
                {data.name[0]}
              </div>
              <span className="text-base font-semibold text-zinc-900">{data.name}</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">
                Best for
              </span>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              {data.verdictCompetitor}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5">
              {data.competitorWins.slice(0, 3).map((w) => (
                <span key={w} className="inline-flex items-center gap-1 text-xs text-zinc-500">
                  <Check className="size-3 text-zinc-400" /> {w}
                </span>
              ))}
            </div>
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(data.name + ' pricing')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              Visit {data.name} <ArrowRight className="size-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
